import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { FinanceAuthProvider } from "@/context/FinanceAuthContext";
import { CompanySettingsProvider } from "@/context/CompanySettingsContext";
import useGlobalUppercase from "@/hooks/useGlobalUppercase";
import Protected from "@/components/Protected";
import FinanceGate from "@/components/FinanceGate";
import Layout from "@/components/Layout";
import InstallPrompt from "@/components/InstallPrompt";
import OnlineStatus from "@/components/OnlineStatus";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Cantieri from "@/pages/Cantieri";
import Manutenzioni from "@/pages/Manutenzioni";
import ProgrammaLavori from "@/pages/ProgrammaLavori";
import IvaContabilita from "@/pages/IvaContabilita";
import CantiereDetail from "@/pages/CantiereDetail";
import RichiestaFornitorePublic from "@/pages/RichiestaFornitorePublic";
import Timbratura from "@/pages/Timbratura";
import Rapportini from "@/pages/Rapportini";
import Bolle from "@/pages/Bolle";
import Fornitori from "@/pages/Fornitori";
import CostiFissi from "@/pages/CostiFissi";
import CostiVariabili from "@/pages/CostiVariabili";
import CashFlow from "@/pages/CashFlow";
import BEP from "@/pages/BEP";
import ReportOre from "@/pages/ReportOre";
import Lavoratori from "@/pages/Lavoratori";
import CedoliniRiepilogo from "@/pages/CedoliniRiepilogo";
import Utenti from "@/pages/Utenti";
import Contabilita from "@/pages/Contabilita";
import SituazioneEconomica from "@/pages/SituazioneEconomica";
import Calendario from "@/pages/Calendario";
import Ferie from "@/pages/Ferie";
import Magazzino from "@/pages/Magazzino";
import RichiesteMagazzino from "@/pages/RichiesteMagazzino";
import RichiesteMateriali from "@/pages/RichiesteMateriali";
import ResourcePool from "@/pages/ResourcePool";
import ImpostazioniAzienda from "@/pages/ImpostazioniAzienda";
import FatturatoPrevisionale from "@/pages/FatturatoPrevisionale";
import AdminTools from "@/pages/AdminTools";
import InviteRegister from "@/pages/InviteRegister";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

function AppRouter() {
  const location = useLocation();
  useGlobalUppercase(); // trasforma tutti gli input in maiuscolo globalmente
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset/:token" element={<ResetPassword />} />
      <Route path="/invite/:token" element={<InviteRegister />} />
      <Route path="/richiesta-fornitore/:token" element={<RichiestaFornitorePublic />} />
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/cantieri" element={<Cantieri />} />
        <Route path="/manutenzioni" element={<Manutenzioni />} />
        <Route path="/opportunita-promemoria" element={<Protected adminOnly><ProgrammaLavori /></Protected>} />
        <Route path="/iva" element={<IvaContabilita />} />
        <Route path="/cantieri/:id" element={<CantiereDetail />} />
        <Route path="/timbratura" element={<Timbratura />} />
        <Route path="/rapportini" element={<Rapportini />} />
        <Route path="/bolle" element={<Bolle />} />
        <Route path="/fornitori" element={<Fornitori />} />
        <Route path="/magazzino" element={<Magazzino />} />
        <Route path="/richieste-magazzino" element={<RichiesteMagazzino />} />
        <Route path="/richieste-materiali" element={<Protected adminOnly><RichiesteMateriali /></Protected>} />
        <Route path="/calendario" element={<Calendario />} />
        <Route path="/ferie" element={<Ferie />} />
        <Route path="/lavoratori" element={<Protected adminOnly><Lavoratori /></Protected>} />
        <Route path="/resource-pool" element={<Protected adminOnly><ResourcePool /></Protected>} />
        <Route path="/impostazioni-azienda" element={<Protected adminOnly><ImpostazioniAzienda /></Protected>} />
        <Route path="/fatturato-previsionale" element={<Protected adminOnly><FatturatoPrevisionale /></Protected>} />
        <Route path="/report-ore" element={<Protected adminOnly><ReportOre /></Protected>} />
        <Route path="/costi-fissi" element={<Protected adminOnly><FinanceGate><CostiFissi /></FinanceGate></Protected>} />
        <Route path="/costi-variabili" element={<Protected adminOnly><FinanceGate><CostiVariabili /></FinanceGate></Protected>} />
        <Route path="/cashflow" element={<Protected adminOnly><FinanceGate><CashFlow /></FinanceGate></Protected>} />
        <Route path="/bep" element={<Protected adminOnly><FinanceGate><BEP /></FinanceGate></Protected>} />
        <Route path="/contabilita" element={<Protected adminOnly><FinanceGate><Contabilita /></FinanceGate></Protected>} />
        <Route path="/situazione" element={<Protected adminOnly><FinanceGate><SituazioneEconomica /></FinanceGate></Protected>} />
        <Route path="/cedolini-riepilogo" element={<Protected adminOnly><FinanceGate><CedoliniRiepilogo /></FinanceGate></Protected>} />
        <Route path="/utenti" element={<Protected adminOnly><Utenti /></Protected>} />
        <Route path="/admin-tools" element={<Protected adminOnly><AdminTools /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <FinanceAuthProvider>
        <CompanySettingsProvider>
          <BrowserRouter>
            <Toaster richColors position="top-right" />
            <OnlineStatus />
            <AppRouter />
            <InstallPrompt />
          </BrowserRouter>
        </CompanySettingsProvider>
      </FinanceAuthProvider>
    </AuthProvider>
  );
}

export default App;
